/**
 * Construccion de las hojas para la exportacion a Excel.
 *
 * Este modulo es CODIGO PURO: recibe los datos y devuelve filas. No importa la
 * libreria de Excel ni toca la base de datos, asi que se puede probar sin
 * navegador. La libreria se usa en excel-export.ts como una capa delgada.
 *
 * DISTINCION IMPORTANTE (Req. 3):
 *   El respaldo JSON es el respaldo OFICIAL y restaurable.
 *   Este archivo de Excel es solo una REPRESENTACION para analizar fuera de la
 *   aplicacion. No sirve para restaurar, y no debe usarse como respaldo.
 *
 * DECISION SOBRE FECHAS:
 *   Se exportan como texto 'AAAA-MM-DD' y no como celdas de fecha de Excel.
 *   Motivo: convertir a fecha implica pasar por husos horarios, que es
 *   precisamente el error que este proyecto evita en todo el modelo. En ese
 *   formato el orden alfabetico coincide con el cronologico, asi que ordenar y
 *   filtrar funciona igual. Los importes y las cantidades SI son numeros
 *   reales, para poder sumarlos y promediarlos.
 */

import { centavosAPesos, type Centavos } from '@/domain/money';
import { nombreDia, sumarDias, type FechaLocal } from '@/domain/dates';
import { formatearCantidadBase, type Dimension } from '@/domain/units';
import {
  DESTINOS_SOBRANTE,
  TIPOS_MOVIMIENTO,
  type MapaTablas,
  type Ingredient,
  type Recipe,
} from './types';

export type ValorCelda = string | number | null;

/** Formato de Excel para importes: se ve como dinero y suma como numero. */
export const FORMATO_DINERO = '"$"#,##0.00';
export const FORMATO_ENTERO = '#,##0';
export const FORMATO_DECIMAL = '#,##0.###';
export const FORMATO_PORCENTAJE = '0.0"%"';

export interface ColumnaExcel {
  titulo: string;
  ancho: number;
  /** Formato numerico de Excel. null = texto o sin formato. */
  formato?: string | null;
}

export interface HojaExcel {
  nombre: string;
  columnas: ColumnaExcel[];
  filas: ValorCelda[][];
  /** Nota que se agrega bajo los datos cuando la hoja aun no tiene contenido. */
  notaVacia?: string;
}

// ---------------------------------------------------------------------------
// Ayudantes
// ---------------------------------------------------------------------------

/** Importe en centavos a numero de pesos, para que Excel pueda sumarlo. */
function dinero(centavos: Centavos | null | undefined): number | null {
  if (centavos === null || centavos === undefined) return null;
  return centavosAPesos(centavos);
}

function siNo(valor: boolean | null | undefined): string {
  if (valor === null || valor === undefined) return '';
  return valor ? 'Si' : 'No';
}

function texto(valor: string | null | undefined): string {
  return valor ?? '';
}

function numero(valor: number | null | undefined): number | null {
  if (valor === null || valor === undefined || !Number.isFinite(valor)) return null;
  return valor;
}

/**
 * Cantidad en unidad legible, por ejemplo "1.5 kg" en lugar de 1500.
 *
 * Se pasa la unidad del insumo para que las cantidades de conteo se lean con su
 * nombre real: "8 barras" y no "8 piezas".
 */
function cantidadLegible(cantidadBase: number, insumo: Ingredient | undefined): string {
  if (insumo === undefined) return String(cantidadBase);
  return formatearCantidadBase(cantidadBase, insumo.dimension, insumo.unidadCompraPreferida);
}

interface Indices {
  insumos: Map<string, Ingredient>;
  recetas: Map<string, Recipe>;
}

function construirIndices(datos: MapaTablas): Indices {
  return {
    insumos: new Map(datos.ingredients.map((i) => [i.id, i])),
    recetas: new Map(datos.recipes.map((r) => [r.id, r])),
  };
}

function nombreInsumo(indices: Indices, id: string): string {
  return indices.insumos.get(id)?.nombre ?? `(insumo eliminado: ${id})`;
}

function insumoDe(indices: Indices, id: string): Ingredient | undefined {
  return indices.insumos.get(id);
}

function nombreReceta(indices: Indices, id: string): string {
  return indices.recetas.get(id)?.nombre ?? `(receta eliminada: ${id})`;
}

/** Costo por unidad base vigente, derivado y nunca almacenado (Req. 7.6). */
function costoPorBase(insumo: Ingredient): number | null {
  if (insumo.stockValuadoBase > 0) {
    return insumo.valorCentavos / insumo.stockValuadoBase;
  }
  return insumo.ultimoCostoPorBase;
}

const NOMBRE_DIMENSION_CORTO: Record<Dimension, string> = {
  masa: 'Peso',
  volumen: 'Volumen',
  conteo: 'Conteo',
};

// ---------------------------------------------------------------------------
// 1. Ingredientes
// ---------------------------------------------------------------------------

function hojaIngredientes(datos: MapaTablas): HojaExcel {
  const filas = datos.ingredients
    .slice()
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
    .map((i) => {
      const unitario = costoPorBase(i);
      const sinCosto = Math.max(0, i.stockBase - i.stockValuadoBase);
      return [
        i.nombre,
        texto(i.categoria),
        NOMBRE_DIMENSION_CORTO[i.dimension],
        i.unidadBase,
        numero(i.stockBase),
        cantidadLegible(i.stockBase, i),
        numero(i.stockValuadoBase),
        numero(sinCosto),
        dinero(i.valorCentavos),
        unitario === null ? null : centavosAPesos(unitario),
        unitario === null ? null : centavosAPesos(unitario * 1000),
        texto(i.unidadCompraPreferida),
        i.factorEquivalencia === null
          ? ''
          : `1 ${i.factorEquivalencia.unidadCompra} = ${i.factorEquivalencia.cantidadBase} ${i.unidadBase}`,
        numero(i.stockMinimoBase),
        siNo(i.activo),
      ] satisfies ValorCelda[];
    });

  return {
    nombre: 'Ingredientes',
    columnas: [
      { titulo: 'Insumo', ancho: 24 },
      { titulo: 'Categoria', ancho: 16 },
      { titulo: 'Tipo de medida', ancho: 14 },
      { titulo: 'Unidad base', ancho: 12 },
      { titulo: 'Existencia', ancho: 12, formato: FORMATO_DECIMAL },
      { titulo: 'Existencia legible', ancho: 16 },
      { titulo: 'Con costo conocido', ancho: 16, formato: FORMATO_DECIMAL },
      { titulo: 'Sin costo', ancho: 12, formato: FORMATO_DECIMAL },
      { titulo: 'Valor inventario', ancho: 16, formato: FORMATO_DINERO },
      { titulo: 'Costo x unidad base', ancho: 18, formato: FORMATO_DINERO },
      { titulo: 'Costo x 1000 unidades', ancho: 20, formato: FORMATO_DINERO },
      { titulo: 'Unidad de compra', ancho: 16 },
      { titulo: 'Equivalencia', ancho: 22 },
      { titulo: 'Minimo deseado', ancho: 14, formato: FORMATO_DECIMAL },
      { titulo: 'Activo', ancho: 8 },
    ],
    filas,
    notaVacia: 'Aun no hay insumos registrados. Se capturan en la Fase 2.',
  };
}

// ---------------------------------------------------------------------------
// 2. Compras
// ---------------------------------------------------------------------------

function hojaCompras(datos: MapaTablas, indices: Indices): HojaExcel {
  const compras = new Map(datos.purchases.map((c) => [c.id, c]));

  const filas = datos.purchaseItems
    .map((renglon) => {
      const compra = compras.get(renglon.purchaseId);
      const unitario =
        renglon.cantidadBase > 0 ? renglon.costoCentavos / renglon.cantidadBase : null;
      const unitarioCaptura =
        renglon.cantidadCaptura > 0 ? renglon.costoCentavos / renglon.cantidadCaptura : null;

      return {
        fecha: compra?.fecha ?? '',
        fila: [
          compra?.fecha ?? '',
          texto(compra?.proveedor),
          nombreInsumo(indices, renglon.ingredientId),
          numero(renglon.cantidadCaptura),
          texto(renglon.unidadCaptura),
          numero(renglon.cantidadBase),
          cantidadLegible(renglon.cantidadBase, insumoDe(indices, renglon.ingredientId)),
          dinero(renglon.costoCentavos),
          unitarioCaptura === null ? null : centavosAPesos(unitarioCaptura),
          unitario === null ? null : centavosAPesos(unitario),
          compra?.anuladaPor === null || compra?.anuladaPor === undefined ? 'No' : 'Si',
          texto(compra?.notas),
        ] satisfies ValorCelda[],
      };
    })
    .sort((a, b) => a.fecha.localeCompare(b.fecha))
    .map((r) => r.fila);

  return {
    nombre: 'Compras',
    columnas: [
      { titulo: 'Fecha', ancho: 12 },
      { titulo: 'Proveedor', ancho: 20 },
      { titulo: 'Insumo', ancho: 24 },
      { titulo: 'Cantidad', ancho: 12, formato: FORMATO_DECIMAL },
      { titulo: 'Unidad', ancho: 10 },
      { titulo: 'Cantidad base', ancho: 14, formato: FORMATO_DECIMAL },
      { titulo: 'Cantidad legible', ancho: 16 },
      { titulo: 'Costo total', ancho: 14, formato: FORMATO_DINERO },
      { titulo: 'Costo x unidad comprada', ancho: 20, formato: FORMATO_DINERO },
      { titulo: 'Costo x unidad base', ancho: 18, formato: FORMATO_DINERO },
      { titulo: 'Anulada', ancho: 10 },
      { titulo: 'Notas', ancho: 30 },
    ],
    filas,
    notaVacia: 'Aun no hay compras registradas. Se capturan en la Fase 2.',
  };
}

// ---------------------------------------------------------------------------
// 3. Inventario (libro de movimientos)
// ---------------------------------------------------------------------------

function hojaInventario(datos: MapaTablas, indices: Indices): HojaExcel {
  const filas = datos.inventoryMovements
    .slice()
    .sort((a, b) => a.fecha.localeCompare(b.fecha) || a.creadoEn.localeCompare(b.creadoEn))
    .map((m) => [
      m.fecha,
      nombreInsumo(indices, m.ingredientId),
      TIPOS_MOVIMIENTO[m.tipo] ?? m.tipo,
      numero(m.cantidadBase),
      cantidadLegible(m.cantidadBase, insumoDe(indices, m.ingredientId)),
      numero(m.cantidadValuadaBase),
      dinero(m.valorCentavos),
      siNo(m.afectaValuacion),
      dinero(m.costoAplicadoCentavos),
      siNo(m.costoImputado),
      texto(m.refTipo),
      texto(m.motivo),
    ] satisfies ValorCelda[]);

  return {
    nombre: 'Inventario',
    columnas: [
      { titulo: 'Fecha', ancho: 12 },
      { titulo: 'Insumo', ancho: 24 },
      { titulo: 'Tipo de movimiento', ancho: 22 },
      { titulo: 'Cantidad', ancho: 12, formato: FORMATO_DECIMAL },
      { titulo: 'Cantidad legible', ancho: 16 },
      { titulo: 'Cantidad valuada', ancho: 16, formato: FORMATO_DECIMAL },
      { titulo: 'Valor', ancho: 14, formato: FORMATO_DINERO },
      { titulo: 'Afecta valuacion', ancho: 16 },
      { titulo: 'Costo reconocido', ancho: 16, formato: FORMATO_DINERO },
      { titulo: 'Costo imputado', ancho: 14 },
      { titulo: 'Origen', ancho: 14 },
      { titulo: 'Motivo', ancho: 30 },
    ],
    filas,
    notaVacia:
      'Aun no hay movimientos de inventario. Se generan al comprar y al producir, en las Fases 2 y 4.',
  };
}

// ---------------------------------------------------------------------------
// 4. Recetas
// ---------------------------------------------------------------------------

function hojaRecetas(datos: MapaTablas, indices: Indices): HojaExcel {
  const porReceta = new Map<string, typeof datos.recipeItems>();
  for (const item of datos.recipeItems) {
    const lista = porReceta.get(item.recipeId) ?? [];
    lista.push(item);
    porReceta.set(item.recipeId, lista);
  }

  const filas: ValorCelda[][] = [];
  const recetasOrdenadas = datos.recipes
    .slice()
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));

  for (const receta of recetasOrdenadas) {
    const items = porReceta.get(receta.id) ?? [];
    const comunes: ValorCelda[] = [
      receta.nombre,
      texto(receta.categoria),
      numero(receta.rendimientoPorciones),
      dinero(receta.precioVentaCentavos),
      numero(receta.diasAnticipacion),
      siNo(receta.activo),
    ];

    if (items.length === 0) {
      filas.push([...comunes, '(sin ingredientes)', null, '', null, '', '']);
      continue;
    }

    for (const item of items) {
      filas.push([
        ...comunes,
        nombreInsumo(indices, item.ingredientId),
        numero(item.cantidadCaptura),
        texto(item.unidadCaptura),
        numero(item.cantidadBase),
        siNo(item.escala),
        texto(item.notas),
      ]);
    }
  }

  return {
    nombre: 'Recetas',
    columnas: [
      { titulo: 'Receta', ancho: 24 },
      { titulo: 'Categoria', ancho: 16 },
      { titulo: 'Rendimiento (porciones)', ancho: 20, formato: FORMATO_DECIMAL },
      { titulo: 'Precio de venta', ancho: 14, formato: FORMATO_DINERO },
      { titulo: 'Dias de anticipacion', ancho: 18, formato: FORMATO_ENTERO },
      { titulo: 'Activa', ancho: 8 },
      { titulo: 'Ingrediente', ancho: 24 },
      { titulo: 'Cantidad', ancho: 12, formato: FORMATO_DECIMAL },
      { titulo: 'Unidad', ancho: 10 },
      { titulo: 'Cantidad base', ancho: 14, formato: FORMATO_DECIMAL },
      { titulo: 'Se escala', ancho: 10 },
      { titulo: 'Notas', ancho: 30 },
    ],
    filas,
    notaVacia: 'Aun no hay recetas registradas. Se capturan en la Fase 3.',
  };
}

// ---------------------------------------------------------------------------
// 5. Menu
// ---------------------------------------------------------------------------

function hojaMenu(datos: MapaTablas, indices: Indices): HojaExcel {
  const filas = datos.plannedItems
    .slice()
    .sort((a, b) => a.fechaVenta.localeCompare(b.fechaVenta))
    .map((p) => {
      const fechaPrep: FechaLocal = sumarDias(p.fechaVenta, -p.diasAnticipacion);
      const ventaEsperada = p.precioVentaCentavos * p.porcionesPlaneadas;
      return [
        p.fechaVenta,
        nombreDia(p.fechaVenta),
        fechaPrep,
        nombreDia(fechaPrep),
        nombreReceta(indices, p.recipeId),
        numero(p.porcionesPlaneadas),
        numero(p.diasAnticipacion),
        dinero(p.precioVentaCentavos),
        dinero(ventaEsperada),
        texto(p.notas),
      ] satisfies ValorCelda[];
    });

  return {
    nombre: 'Menu',
    columnas: [
      { titulo: 'Fecha de venta', ancho: 14 },
      { titulo: 'Dia de venta', ancho: 12 },
      { titulo: 'Fecha de preparacion', ancho: 18 },
      { titulo: 'Dia de preparacion', ancho: 16 },
      { titulo: 'Platillo', ancho: 24 },
      { titulo: 'Porciones planeadas', ancho: 18, formato: FORMATO_DECIMAL },
      { titulo: 'Dias de anticipacion', ancho: 18, formato: FORMATO_ENTERO },
      { titulo: 'Precio de venta', ancho: 14, formato: FORMATO_DINERO },
      { titulo: 'Venta esperada', ancho: 14, formato: FORMATO_DINERO },
      { titulo: 'Notas', ancho: 30 },
    ],
    filas,
    notaVacia: 'Aun no hay menu registrado. Se captura en la Fase 4.',
  };
}

// ---------------------------------------------------------------------------
// 6. Produccion
// ---------------------------------------------------------------------------

const NOMBRE_ESTADO: Record<string, string> = {
  pendiente: 'Pendiente',
  en_proceso: 'En proceso',
  completada: 'Completada',
  cancelada: 'Cancelada',
};

function hojaProduccion(datos: MapaTablas, indices: Indices): HojaExcel {
  const filas = datos.productions
    .slice()
    .sort((a, b) => a.fechaPrep.localeCompare(b.fechaPrep))
    .map((p) => {
      const costoPorPorcion =
        p.costoTotalCentavos !== null && p.porcionesAProducir > 0
          ? p.costoTotalCentavos / p.porcionesAProducir
          : null;
      return [
        p.fechaPrep,
        nombreDia(p.fechaPrep),
        p.fechaVenta,
        nombreDia(p.fechaVenta),
        nombreReceta(indices, p.recipeId),
        numero(p.porcionesAProducir),
        NOMBRE_ESTADO[p.estado] ?? p.estado,
        texto(p.completadaEn),
        dinero(p.costoTotalCentavos),
        costoPorPorcion === null ? null : centavosAPesos(costoPorPorcion),
        siNo(p.costoEstimado),
        siNo(p.costoIncompleto),
      ] satisfies ValorCelda[];
    });

  return {
    nombre: 'Produccion',
    columnas: [
      { titulo: 'Fecha de preparacion', ancho: 18 },
      { titulo: 'Dia', ancho: 12 },
      { titulo: 'Fecha de venta', ancho: 14 },
      { titulo: 'Dia de venta', ancho: 12 },
      { titulo: 'Platillo', ancho: 24 },
      { titulo: 'Porciones', ancho: 12, formato: FORMATO_DECIMAL },
      { titulo: 'Estado', ancho: 14 },
      { titulo: 'Completada en', ancho: 22 },
      { titulo: 'Costo total', ancho: 14, formato: FORMATO_DINERO },
      { titulo: 'Costo x porcion', ancho: 14, formato: FORMATO_DINERO },
      { titulo: 'Costo estimado', ancho: 14 },
      { titulo: 'Costo incompleto', ancho: 16 },
    ],
    filas,
    notaVacia: 'Aun no hay produccion registrada. Se captura en la Fase 4.',
  };
}

// ---------------------------------------------------------------------------
// 7. Ventas
// ---------------------------------------------------------------------------

function hojaVentas(datos: MapaTablas, indices: Indices): HojaExcel {
  const destinosPorPlan = new Map<string, string[]>();
  for (const d of datos.saleDispositions) {
    const lista = destinosPorPlan.get(d.plannedItemId) ?? [];
    lista.push(`${DESTINOS_SOBRANTE[d.tipo] ?? d.tipo}: ${d.porciones}`);
    destinosPorPlan.set(d.plannedItemId, lista);
  }

  const planes = new Map(datos.plannedItems.map((p) => [p.id, p]));

  const filas = datos.sales
    .slice()
    .sort((a, b) => a.fecha.localeCompare(b.fecha))
    .map((v) => {
      const plan = v.plannedItemId === null ? undefined : planes.get(v.plannedItemId);
      const planeadas = plan?.porcionesPlaneadas ?? null;
      const noVendidas =
        planeadas === null ? null : Math.max(0, planeadas - v.porcionesVendidas);
      const ganancia =
        v.costoCentavos === null ? null : v.ingresoCentavos - v.costoCentavos;
      const margen =
        v.costoCentavos === null || v.ingresoCentavos === 0
          ? null
          : ((v.ingresoCentavos - v.costoCentavos) / v.ingresoCentavos) * 100;

      return [
        v.fecha,
        nombreDia(v.fecha),
        nombreReceta(indices, v.recipeId),
        planeadas,
        numero(v.porcionesVendidas),
        noVendidas,
        dinero(v.precioUnitarioCentavos),
        dinero(v.ingresoCentavos),
        dinero(v.costoCentavos),
        dinero(ganancia),
        margen === null ? null : Number(margen.toFixed(1)),
        v.plannedItemId === null ? '' : (destinosPorPlan.get(v.plannedItemId)?.join(' · ') ?? ''),
        v.corrigeA === null ? 'No' : 'Si',
        texto(v.notas),
      ] satisfies ValorCelda[];
    });

  return {
    nombre: 'Ventas',
    columnas: [
      { titulo: 'Fecha', ancho: 12 },
      { titulo: 'Dia', ancho: 12 },
      { titulo: 'Platillo', ancho: 24 },
      { titulo: 'Porciones planeadas', ancho: 18, formato: FORMATO_DECIMAL },
      { titulo: 'Porciones vendidas', ancho: 18, formato: FORMATO_DECIMAL },
      { titulo: 'No vendidas', ancho: 12, formato: FORMATO_DECIMAL },
      { titulo: 'Precio unitario', ancho: 14, formato: FORMATO_DINERO },
      { titulo: 'Ingreso', ancho: 14, formato: FORMATO_DINERO },
      { titulo: 'Costo', ancho: 14, formato: FORMATO_DINERO },
      { titulo: 'Ganancia', ancho: 14, formato: FORMATO_DINERO },
      { titulo: 'Margen', ancho: 10, formato: FORMATO_PORCENTAJE },
      { titulo: 'Destino de sobrantes', ancho: 26 },
      { titulo: 'Es correccion', ancho: 14 },
      { titulo: 'Notas', ancho: 30 },
    ],
    filas,
    notaVacia: 'Aun no hay ventas registradas. Se capturan en la Fase 5.',
  };
}

// ---------------------------------------------------------------------------
// Armado del libro
// ---------------------------------------------------------------------------

/**
 * Hojas que todavia no se construyen. Se declaran aqui para que quede explicito
 * que faltan, en lugar de omitirlas en silencio.
 */
export const HOJAS_PENDIENTES: ReadonlyArray<{ nombre: string; fase: number; motivo: string }> = [
  {
    nombre: 'Costos de recetas',
    fase: 3,
    motivo: 'Necesita el motor de costeo: costo por porcion, ganancia y margen por receta.',
  },
  {
    nombre: 'Ganancias',
    fase: 5,
    motivo:
      'Necesita el motor de ganancias: ingresos, costo de lo vendido y de la merma por periodo.',
  },
  {
    nombre: 'Pedidos',
    fase: 6,
    motivo:
      'Se puede construir con los datos que ya hay; quedo fuera de la etapa 1 del modulo de ' +
      'pedidos para no tocar la exportacion existente. Los pedidos SI se guardan en el respaldo.',
  },
];

/** Construye las 7 hojas disponibles a partir de los datos. */
export function construirHojas(datos: MapaTablas): HojaExcel[] {
  const indices = construirIndices(datos);
  return [
    hojaVentas(datos, indices),
    hojaCompras(datos, indices),
    hojaInventario(datos, indices),
    hojaIngredientes(datos),
    hojaRecetas(datos, indices),
    hojaMenu(datos, indices),
    hojaProduccion(datos, indices),
  ];
}

/** `"control-negocio-comida-2026-08-17-1430.analisis.xlsx"` */
export function nombreArchivoExcel(fecha = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  const sello =
    `${fecha.getFullYear()}-${p(fecha.getMonth() + 1)}-${p(fecha.getDate())}` +
    `-${p(fecha.getHours())}${p(fecha.getMinutes())}`;
  return `control-negocio-comida-${sello}.analisis.xlsx`;
}
